import React, { useState } from 'react';
import axios from 'axios';
import { Search, Loader2, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { HumanLoader } from './HumanLoader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POSITIONS = [
  'Deputado Federal',
  'Deputado Estadual',
  'Senador',
  'Governador',
  'Prefeito',
  'Presidente',
  'Vereador',
];

const SearchForm = ({ onAnalysisComplete, onUpgradeRequired }) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    state: '',
    additional_info: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!user) {
      toast.info('Faça login para consultar candidatos (até 3 consultas grátis).');
      navigate('/auth');
      return;
    }

    if (!formData.name.trim()) {
      setError('Informe o nome do candidato');
      return;
    }
    if (!formData.position) {
      setError('Selecione o cargo pretendido');
      return;
    }
    if (!formData.state.trim()) {
      setError('Informe o estado');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/candidates/analyze`, {
        ...formData,
        name: formData.name.trim(),
        state: formData.state.trim(),
        additional_info: formData.additional_info.trim() || undefined,
      }, {
        timeout: 130000,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });

      toast.success('Análise concluída com sucesso!');
      onAnalysisComplete(response.data);
      setFormData({ name: '', position: '', state: '', additional_info: '' });
    } catch (err) {
      console.error('Error analyzing candidate:', err);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      if (status === 401) {
        setError('Sessão expirada. Faça login novamente.');
        toast.error('Faça login para consultar candidatos');
        navigate('/auth');
      } else if (status === 402) {
        const msg = typeof detail === 'string'
          ? detail
          : 'Você já consultou 3 políticos este mês. Para continuar consultando sem limite, assine o plano completo por R$ 14,90/mês.';
        setError(msg);
        toast.error('Limite gratuito atingido');
        // Show the elegant upgrade modal in-place instead of navigating away.
        if (typeof onUpgradeRequired === 'function') {
          onUpgradeRequired(msg);
        } else {
          setTimeout(() => navigate('/planos'), 1200);
        }
      } else if (status === 404) {
        setError('Ops! Não encontramos esse político agora. Tente o nome completo ou o número de urna.');
        toast.error('Não encontramos esse político');
      } else if (status === 429) {
        setError('Muitas consultas em sequência. Aguarde alguns instantes e tente novamente.');
        toast.error('Muitas requisições');
      } else if (status === 504) {
        setError('A análise demorou mais que o esperado. Tente novamente.');
        toast.error('Tempo esgotado na análise');
      } else if (status === 502) {
        setError('A IA retornou uma resposta inválida. Tente novamente.');
        toast.error('Erro de resposta da IA');
      } else if (err.code === 'ECONNABORTED') {
        setError('Conexão expirada. Tente novamente.');
        toast.error('Timeout de conexão');
      } else {
        setError(detail || 'Erro ao analisar candidato. Tente novamente.');
        toast.error('Erro na análise');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-4 sm:p-6 bg-white border border-[#E2E8F0] rounded-md shadow-sm">
      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
        {error && (
          <div className="flex items-start gap-3 p-4 bg-[#FFEBEE] border border-[#C62828]/20 rounded-md" data-testid="form-error">
            <AlertCircle className="w-5 h-5 text-[#C62828] shrink-0 mt-0.5" />
            <p className="text-sm text-[#C62828]">{error}</p>
          </div>
        )}

        <div>
          <Label htmlFor="name" className="text-xs font-bold text-[#0F1C2E] uppercase tracking-wider">
            Nome do Candidato *
          </Label>
          <Input
            id="name"
            data-testid="search-candidate-input"
            value={formData.name}
            onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setError(null); }}
            placeholder="Nome do candidato ou número de urna..."
            disabled={loading}
            className="mt-2 border-[#E2E8F0] focus:border-[#006030] focus:ring-[#006030]"
          />
        </div>

        <div>
          <Label htmlFor="position" className="text-xs font-bold text-[#0F1C2E] uppercase tracking-wider">
            Cargo Pretendido *
          </Label>
          <Select
            value={formData.position}
            onValueChange={(value) => { setFormData({ ...formData, position: value }); setError(null); }}
            disabled={loading}
          >
            <SelectTrigger data-testid="position-select" className="mt-2 border-[#E2E8F0] focus:border-[#006030] focus:ring-[#006030]">
              <SelectValue placeholder="Selecione o cargo" />
            </SelectTrigger>
            <SelectContent>
              {POSITIONS.map((pos) => (
                <SelectItem key={pos} value={pos}>{pos}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="state" className="text-xs font-bold text-[#0F1C2E] uppercase tracking-wider">
            Estado *
          </Label>
          <Input
            id="state"
            data-testid="state-input"
            value={formData.state}
            onChange={(e) => { setFormData({ ...formData, state: e.target.value }); setError(null); }}
            placeholder="Ex: São Paulo"
            disabled={loading}
            className="mt-2 border-[#E2E8F0] focus:border-[#006030] focus:ring-[#006030]"
          />
        </div>

        <div>
          <Label htmlFor="additional_info" className="text-xs font-bold text-[#0F1C2E] uppercase tracking-wider">
            Informações Adicionais
          </Label>
          <Textarea
            id="additional_info"
            data-testid="additional-info-input"
            value={formData.additional_info}
            onChange={(e) => setFormData({ ...formData, additional_info: e.target.value })}
            placeholder="Informações extras que podem ajudar na análise..."
            disabled={loading}
            className="mt-2 border-[#E2E8F0] focus:border-[#006030] focus:ring-[#006030] min-h-[80px]"
          />
        </div>

        <Button
          type="submit"
          data-testid="analyze-button"
          disabled={loading}
          className="w-full bg-[#006030] hover:bg-[#004020] text-white font-medium py-3 h-12 rounded-md transition-all duration-200"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Consultando dados oficiais...
            </>
          ) : (
            <>
              <Search className="w-5 h-5 mr-2" />
              Ver tudo que descobrimos
            </>
          )}
        </Button>
        {loading && (
          <HumanLoader active={loading} className="justify-center pt-2" />
        )}
      </form>
    </Card>
  );
};

export default SearchForm;
